import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { formatVND } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Phone, MapPin, Clock, Banknote, Check, CircleDot, X, Pencil, Trash2, Users, BadgeCheck, CircleDollarSign } from "lucide-react";
import PinDialog from "@/components/PinDialog";

const HOUR_WIDTH = 120;
const QUARTER_WIDTH = HOUR_WIDTH / 4;
const START_HOUR = 5;
const END_HOUR = 24;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const ROW_HEIGHT = 72;
const PX_PER_MIN = HOUR_WIDTH / 60;
const SIDEBAR_WIDTH = 180;

const STATUS_CFG = {
  confirmed: { tint: "rgba(34,197,94,0.08)", bl: "#34d399", iconClr: "#34d399", iconKey: "confirmed", op: 1, hdr: "#22c55e" },
  pending:   { tint: "rgba(251,191,36,0.08)", bl: "#fbbf24", iconClr: "#fbbf24", iconKey: "pending", op: 1, hdr: "#f59e0b" },
  completed: { tint: "rgba(96,165,250,0.08)", bl: "#60a5fa", iconClr: "#60a5fa", iconKey: "completed", op: 0.85, hdr: "#3b82f6" },
  cancelled: { tint: "rgba(239,68,68,0.06)", bl: "#ef4444", iconClr: "#ef4444", iconKey: "cancelled", op: 0.55, hdr: "#ef4444" },
};
const SOCIAL_CFG = { tint: "rgba(45,212,191,0.08)", bl: "#2dd4bf", iconClr: "#2dd4bf", iconKey: "social", op: 1, hdr: "#0d9488" };
const STATUS_LABELS = { confirmed: "Đã xác nhận", pending: "Chờ xác nhận", completed: "Hoàn thành", cancelled: "Đã huỷ" };

function timeToMin(t) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function minToTime(m) { return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; }

function StatusCircle({ iconKey, color, size = 28 }) {
  const iconStyle = { width: size * 0.5, height: size * 0.5, strokeWidth: 2.5 };
  let Icon = Check;
  if (iconKey === "pending") Icon = Clock;
  else if (iconKey === "completed") Icon = CircleDot;
  else if (iconKey === "cancelled") Icon = X;
  else if (iconKey === "social") Icon = Users;
  else if (iconKey === "unpaid") Icon = Banknote;
  else if (iconKey === "awaiting") Icon = CircleDollarSign;
  return (
    <div className="shrink-0 flex items-center justify-center rounded-full" style={{ width: size, height: size, border: `2px solid ${color}`, background: `${color}15` }}>
      <Icon style={{ ...iconStyle, color }} />
    </div>
  );
}

function BookingBar({ booking, onDragStart, isDraggingThis, hidePrice, hideName, isNewest }) {
  const isSocial = booking.booking_type === "social";
  const cfg = isSocial ? SOCIAL_CFG : (STATUS_CFG[booking.status] || STATUS_CFG.pending);
  const sMin = timeToMin(booking.start_time) - START_HOUR * 60;
  const eMin = timeToMin(booking.end_time) - START_HOUR * 60;
  const dur = (eMin - sMin) / 60;
  const widthPx = Math.max(eMin - sMin, 15) * PX_PER_MIN;
  const isNarrow = dur < 1.5;
  const isVeryNarrow = dur < 1;
  const name = booking.customer_name || booking.organizer_name || "";
  const shortName = isVeryNarrow ? (name.split(" ").pop() || name) : isNarrow ? (name.split(" ").slice(-2).join(" ") || name) : name;
  const price = booking.total_price || booking.social_total_price || 0;
  const isCancelled = booking.status === "cancelled";
  const isPending = booking.status === "pending";

  // Payment status icon override — applies to ALL confirmed bookings (regular + social)
  // Exception: bookings auto-generated from groups (hội)
  const isFromGroup = (booking.notes || "").includes("Booking tự động từ hội");
  const isConfirmedAny = booking.status === "confirmed" && !isFromGroup;
  const isPaid = isConfirmedAny && booking.payment_verified_at;
  const isAwaitingVerify = isConfirmedAny && !booking.payment_verified_at && booking.payment_confirmed_at;
  const isUnpaid = isConfirmedAny && !booking.payment_verified_at && !booking.payment_confirmed_at;

  let barTint = cfg.tint;
  let barBorder = cfg.bl;
  let displayIconKey = cfg.iconKey;
  let displayIconClr = cfg.iconClr;

  if (isAwaitingVerify) {
    barTint = "rgba(168,85,247,0.10)";  // purple
    barBorder = "#a855f7";
    displayIconKey = "awaiting";
    displayIconClr = "#a855f7";
  } else if (isUnpaid) {
    barTint = "rgba(249,115,22,0.08)";  // orange
    barBorder = "#f97316";
    displayIconKey = "unpaid";
    displayIconClr = "#f97316";
  }

  // Pulse animation for ALL unpaid/awaiting bookings (not just pending)
  const shouldPulse = isPending || isUnpaid || isAwaitingVerify;

  return (
    <div data-booking-bar="true"
      onMouseDown={(e) => { e.stopPropagation(); onDragStart(e, booking); }}
      onClick={(e) => e.stopPropagation()}
      className={`absolute select-none z-10 ${isDraggingThis ? "opacity-30" : "cursor-grab active:cursor-grabbing"} ${shouldPulse ? "animate-pending-pulse" : ""} ${isNewest ? "animate-newest-booking" : ""}`}
      style={{
        left: `${sMin * PX_PER_MIN}px`, width: `${widthPx}px`, top: 3, bottom: 3,
        background: isNewest ? "rgba(45,212,191,0.18)" : barTint,
        borderLeft: `3px solid ${isNewest ? "#2dd4bf" : barBorder}`,
        border: `1px solid ${isNewest ? "#2dd4bf" : `${barBorder}30`}`,
        borderLeftWidth: isNewest ? 4 : 3, borderLeftColor: isNewest ? "#2dd4bf" : barBorder,
        borderRadius: 14, padding: isVeryNarrow ? "4px 6px" : "6px 12px",
        overflow: "hidden", opacity: cfg.op,
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        boxShadow: isNewest
          ? "0 0 0 2px rgba(45,212,191,0.45), 0 4px 16px rgba(45,212,191,0.35)"
          : "0 2px 8px rgba(0,0,0,0.2)",
        transition: "box-shadow 0.15s, transform 0.15s",
        display: "flex", alignItems: "center", gap: 8,
        zIndex: isNewest ? 15 : 10,
      }}
      data-testid={`booking-bar-${booking.id}`}
      data-newest={isNewest ? "true" : undefined}
      title={`${name} ${booking.start_time}-${booking.end_time}${isNewest ? " (Mới nhất)" : ""}`}
      onMouseEnter={(e) => { if (!isDraggingThis) { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.35)"; e.currentTarget.style.transform = "scaleY(1.03)"; } }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = isNewest ? "0 0 0 2px rgba(45,212,191,0.45), 0 4px 16px rgba(45,212,191,0.35)" : "0 2px 8px rgba(0,0,0,0.2)"; e.currentTarget.style.transform = "scaleY(1)"; }}
    >
      {/* Left: Text content */}
      <div className="pointer-events-none flex-1 min-w-0 overflow-hidden" style={{ lineHeight: 1.3 }}>
        {!hideName && (
          <div data-pdf-name="true" className={`text-[13px] font-bold text-slate-100 ${isCancelled ? "line-through opacity-50" : ""}`} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{shortName}</div>
        )}
        {!isVeryNarrow && (
          <div className="text-[11px] text-slate-400 tabular-nums mt-px">{booking.start_time}-{booking.end_time}</div>
        )}
        {!isNarrow && !hidePrice && (
          <div data-pdf-price="true" className="text-[11px] text-slate-400 mt-px">{formatVND(price)} đ</div>
        )}
      </div>
      {/* Right: Status icon circle */}
      {!isVeryNarrow && (
        <div className="pointer-events-none shrink-0">
          <StatusCircle iconKey={displayIconKey} color={displayIconClr} size={isNarrow ? 24 : 28} />
        </div>
      )}
      {/* Badge "MỚI" cho booking mới nhất trong ngày */}
      {isNewest && !isVeryNarrow && (
        <div
          data-testid={`newest-badge-${booking.id}`}
          className="pointer-events-none absolute -top-1.5 -right-1.5 bg-teal-400 text-slate-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md"
          style={{ letterSpacing: 0.3 }}
        >
          MỚI
        </div>
      )}
    </div>
  );
}

// Social summary blocks for collapsed view
function SocialSummaryBlocks({ socialBookings, hours }) {
  const blocks = useMemo(() => {
    const active = socialBookings.filter(b => b.status !== "cancelled");
    if (!active.length) return [];
    // Build per-30min slot data
    const slots = {};
    active.forEach(b => {
      const s = timeToMin(b.start_time), e = timeToMin(b.end_time);
      for (let m = s; m < e; m += 30) {
        if (!slots[m]) slots[m] = { count: 0, guests: 0 };
        slots[m].count += 1;
        slots[m].guests += (b.social_guest_count || 1);
      }
    });
    // Merge adjacent slots
    const keys = Object.keys(slots).map(Number).sort((a, b) => a - b);
    if (!keys.length) return [];
    const merged = [];
    let cur = { start: keys[0], end: keys[0] + 30, guests: slots[keys[0]].guests, count: slots[keys[0]].count };
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i];
      if (k === cur.end) {
        cur.end = k + 30;
        cur.guests = Math.max(cur.guests, slots[k].guests);
        cur.count = Math.max(cur.count, slots[k].count);
      } else {
        merged.push(cur);
        cur = { start: k, end: k + 30, guests: slots[k].guests, count: slots[k].count };
      }
    }
    merged.push(cur);
    return merged;
  }, [socialBookings]);

  return (
    <>
      {blocks.map((b, i) => {
        const left = (b.start - START_HOUR * 60) * PX_PER_MIN;
        const width = (b.end - b.start) * PX_PER_MIN;
        return (
          <div key={i} className="absolute flex items-center justify-center rounded-md pointer-events-none"
            style={{ left, width, top: 12, bottom: 12, background: "linear-gradient(135deg,#0d9488,#0f766e)", border: "1px solid #2dd4bf", boxShadow: "0 0 12px rgba(45,212,191,0.3)" }}>
            <span className="text-teal-100 text-xs font-bold drop-shadow-md">👥 {b.guests}</span>
          </div>
        );
      })}
    </>
  );
}

// Quick action popup - rendered via portal to body
function BookingPopup({ booking, position, onClose, onStatusChange, onEdit, onDelete, onVerifyPayment, onUnverifyPayment }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  if (!booking) return null;
  const isSocial = booking.booking_type === "social";
  const cfg = isSocial ? SOCIAL_CFG : (STATUS_CFG[booking.status] || STATUS_CFG.pending);
  const dur = (timeToMin(booking.end_time) - timeToMin(booking.start_time)) / 60;
  const price = booking.total_price || booking.social_total_price || 0;

  // Use React portal to render directly in document.body
  return createPortal(
    <div ref={ref} data-testid="booking-popup"
      style={{ position: "fixed", left: position.x, top: position.y, transform: "translateX(-50%)", width: 300, zIndex: 9999, borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.5)", animation: "popupIn 0.15s ease-out" }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: cfg.hdr }}>
        <span className="text-white font-bold text-sm truncate max-w-[180px]">{booking.customer_name || booking.organizer_name}</span>
        <span className="bg-white/25 text-white text-[11px] px-2.5 py-0.5 rounded-full font-medium">{STATUS_LABELS[booking.status]}</span>
      </div>
      <div className="bg-[#1e293b] px-4 py-3 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-400"><Phone className="w-3.5 h-3.5" /> {booking.customer_phone || "—"}</div>
        <div className="flex items-center gap-2 text-slate-400">
          <MapPin className="w-3.5 h-3.5" /> {booking.court_name}
          {isSocial && <span className="bg-teal-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">SOCIAL</span>}
        </div>
        <div className="flex items-center gap-2 text-slate-400"><Clock className="w-3.5 h-3.5" /> {booking.start_time} - {booking.end_time} ({dur}h)</div>
        <div className="flex items-center gap-2 text-white font-bold text-base"><Banknote className="w-4 h-4 text-lime-400" /> {formatVND(price)} VNĐ</div>
        {/* Payment status */}
        {booking.payment_verified_at ? (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-emerald-400 text-xs"><BadgeCheck className="w-3.5 h-3.5" /> Đã XN thanh toán</span>
            {onUnverifyPayment && (
              <button onClick={() => { onUnverifyPayment(booking.id); onClose(); }}
                className="text-[10px] text-slate-500 hover:text-red-400 font-medium transition-colors"
                data-testid="popup-unverify-payment">
                Hoàn tác
              </button>
            )}
          </div>
        ) : booking.payment_confirmed_at ? (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-amber-400 text-xs"><CircleDollarSign className="w-3.5 h-3.5" /> Chờ XN thanh toán</span>
            {onVerifyPayment && (
              <button onClick={() => { onVerifyPayment(booking.id); onClose(); }}
                className="text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-2.5 py-1 rounded-md transition-colors"
                data-testid="popup-verify-payment">
                XN thanh toán
              </button>
            )}
          </div>
        ) : null}
      </div>
      <div className="bg-[#0f172a] px-4 py-3 border-t border-[#334155] space-y-3" style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
        <p className="text-[10px] text-slate-500 font-bold tracking-wider uppercase">Thao tác nhanh</p>
        <div className="flex gap-4">
          {booking.status !== "confirmed" && <button onClick={() => onStatusChange(booking.id, "confirmed")} className="flex items-center gap-1 text-lime-400 text-xs font-medium hover:text-lime-300" data-testid="popup-confirm"><Check className="w-3.5 h-3.5" /> Xác nhận</button>}
          {booking.status !== "completed" && <button onClick={() => onStatusChange(booking.id, "completed")} className="flex items-center gap-1 text-sky-400 text-xs font-medium hover:text-sky-300" data-testid="popup-complete"><CircleDot className="w-3.5 h-3.5" /> Hoàn thành</button>}
          {booking.status !== "cancelled" && <button onClick={() => onStatusChange(booking.id, "cancelled")} className="flex items-center gap-1 text-red-400 text-xs font-medium hover:text-red-300" data-testid="popup-cancel"><X className="w-3.5 h-3.5" /> Huỷ</button>}
        </div>
        <div className="flex gap-2 pt-2 border-t border-[#334155]">
          <button onClick={() => { onClose(); onEdit(booking); }} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-[#334155] hover:bg-[#475569] text-white text-xs rounded-md" data-testid="popup-edit"><Pencil className="w-3 h-3" /> Chỉnh sửa</button>
          <button onClick={() => onDelete(booking.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-red-900/50 hover:bg-red-900/70 text-red-300 text-xs rounded-md" data-testid="popup-delete"><Trash2 className="w-3 h-3" /> Xóa</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function TimelineGrid({ courts, bookings, selectedDate, onSlotClick, onSocialSlotClick, onBookingClick, onDeleteBooking, onSaved, updateBookingMutation, hidePrice, hideName, onVerifyPayment, onUnverifyPayment, newestBookingId }) {
  const scrollRef = useRef(null);
  const gridContainerRef = useRef(null);
  const rowRefs = useRef({});
  const [currentMinute, setCurrentMinute] = useState(() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); });
  const [socialExpanded, setSocialExpanded] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [optimisticBookings, setOptimisticBookings] = useState(null);
  const justDraggedRef = useRef(false);
  const [popupBooking, setPopupBooking] = useState(null);
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
  const hoverHighlightRef = useRef(null);
  const hoverLineRef = useRef(null);
  const hoverTooltipRef = useRef(null);
  const dragRef = useRef({ active: false, bookingId: null, booking: null, isSocial: false, duration: 0,
    grabOffsetMinutes: 0, cloneEl: null, tooltipEl: null, highlightEl: null,
    origCourtId: null, origStart: null, origEnd: null, newCourtId: null, newStart: null, newEnd: null,
    startX: 0, startY: 0, grabClientOffsetX: 0, grabClientOffsetY: 0 });

  useEffect(() => { const iv = setInterval(() => { const n = new Date(); setCurrentMinute(n.getHours() * 60 + n.getMinutes()); }, 60000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, (currentMinute - START_HOUR * 60 - 60) * PX_PER_MIN); /* eslint-disable-next-line */ }, []);

  const isWeekend = useMemo(() => { const d = new Date(selectedDate + "T00:00:00"); return d.getDay() === 0 || d.getDay() === 6; }, [selectedDate]);
  const PEAK_HOUR_START = 17;
  const PEAK_HOUR_END = 19;
  const peakLeftPx = (PEAK_HOUR_START - START_HOUR) * HOUR_WIDTH;
  const peakWidthPx = (PEAK_HOUR_END - PEAK_HOUR_START) * HOUR_WIDTH;
  const socialCourt = courts.find(c => c.court_type === "social");
  const regularCourts = courts.filter(c => c.court_type !== "social");
  const displayBookings = optimisticBookings || bookings;
  const bookingsByCourtId = useMemo(() => { const m = {}; for (const b of displayBookings) { if (!m[b.court_id]) m[b.court_id] = []; m[b.court_id].push(b); } return m; }, [displayBookings]);
  const courtRevenue = (cid) => (bookingsByCourtId[cid] || []).filter(b => b.status !== "cancelled").reduce((s, b) => s + (b.total_price || 0), 0);
  const courtBookingCount = (cid) => (bookingsByCourtId[cid] || []).filter(b => b.status !== "cancelled").length;

  const cleanupDrag = useCallback(() => {
    const d = dragRef.current;
    if (d.cloneEl) { d.cloneEl.remove(); d.cloneEl = null; }
    if (d.tooltipEl) { d.tooltipEl.remove(); d.tooltipEl = null; }
    if (d.highlightEl) { d.highlightEl.remove(); d.highlightEl = null; }
    Object.values(rowRefs.current).forEach(el => { if (el) el.classList.remove("drag-over-row"); });
    d.active = false; d.bookingId = null;
  }, []);

  const clientXToMinutes = useCallback((clientX) => {
    const firstKey = Object.keys(rowRefs.current)[0];
    const firstEl = firstKey ? rowRefs.current[firstKey] : null;
    if (!firstEl) return START_HOUR * 60;
    const areaEl = firstEl.querySelector('[data-testid^="timeline-area-"]');
    if (!areaEl) return START_HOUR * 60;
    return ((clientX - areaEl.getBoundingClientRect().left) / PX_PER_MIN) + START_HOUR * 60;
  }, []);

  const findCourtAtY = useCallback((clientY) => {
    for (const [courtId, el] of Object.entries(rowRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY < r.bottom) return courts.find(c => c.id === courtId) || null;
    }
    return null;
  }, [courts]);

  // Hover
  const handleTimelineMouseMove = useCallback((e) => {
    if (dragRef.current.active) { [hoverHighlightRef, hoverLineRef, hoverTooltipRef].forEach(r => { if (r.current) r.current.style.display = "none"; }); return; }
    const cr = gridContainerRef.current?.getBoundingClientRect();
    if (!cr) return;
    const timelineX = (e.clientX - cr.left) - SIDEBAR_WIDTH;
    if (timelineX < 0) { [hoverHighlightRef, hoverLineRef, hoverTooltipRef].forEach(r => { if (r.current) r.current.style.display = "none"; }); return; }
    // Raw position for line (no snap - follows cursor exactly)
    const rawPx = e.clientX - cr.left;
    // Snapped position for tooltip text
    const rawMin = (timelineX / PX_PER_MIN) + START_HOUR * 60;
    const snapped = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, Math.round(rawMin / 15) * 15));
    const snapPx = ((snapped - START_HOUR * 60) * PX_PER_MIN) + SIDEBAR_WIDTH;
    const ts = minToTime(snapped);
    if (hoverHighlightRef.current) { hoverHighlightRef.current.style.display = "block"; hoverHighlightRef.current.style.left = `${snapPx}px`; }
    if (hoverLineRef.current) { hoverLineRef.current.style.display = "block"; hoverLineRef.current.style.left = `${rawPx}px`; }
    if (hoverTooltipRef.current) { hoverTooltipRef.current.style.display = "block"; hoverTooltipRef.current.style.left = `${rawPx - 22}px`; hoverTooltipRef.current.textContent = ts; }
  }, []);
  const handleTimelineMouseLeave = useCallback(() => { [hoverHighlightRef, hoverLineRef, hoverTooltipRef].forEach(r => { if (r.current) r.current.style.display = "none"; }); }, []);

  // Popup actions - optimistic via mutation
  const handleStatusChange = useCallback(async (bookingId, newStatus) => {
    if (updateBookingMutation) {
      updateBookingMutation.mutate(
        { bookingId, data: { status: newStatus } },
        { onSuccess: () => toast.success(STATUS_LABELS[newStatus] || "Đã cập nhật"), onError: () => toast.error("Lỗi cập nhật trạng thái") }
      );
    } else {
      try { await api.put(`/bookings/${bookingId}`, { status: newStatus }); toast.success(STATUS_LABELS[newStatus] || "Đã cập nhật"); if (onSaved) onSaved(); }
      catch { toast.error("Lỗi cập nhật trạng thái"); }
    }
    setPopupBooking(null);
  }, [onSaved, updateBookingMutation]);
  const handlePopupDelete = useCallback(async (bookingId) => {
    setPopupBooking(null); // Close popup first so it doesn't cover PinDialog
    setPendingDeleteId(bookingId);
    setDeletePinOpen(true);
  }, []);
  const [deletePinOpen, setDeletePinOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const doDeleteBooking = useCallback(() => {
    setPopupBooking(null);
    if (pendingDeleteId && onDeleteBooking) onDeleteBooking(pendingDeleteId);
    setPendingDeleteId(null);
  }, [pendingDeleteId, onDeleteBooking]);

  // Calculate popup position close to bar
  const calcPopupPos = useCallback((barEl) => {
    if (!barEl) return { x: 300, y: 300 };
    const r = barEl.getBoundingClientRect();
    const pw = 300, ph = 280;
    let x = r.left + r.width / 2;
    if (x - pw / 2 < 10) x = pw / 2 + 10;
    if (x + pw / 2 > window.innerWidth - 10) x = window.innerWidth - pw / 2 - 10;
    let y = r.bottom + 4;
    if (y + ph > window.innerHeight - 20) y = r.top - ph - 4;
    return { x, y };
  }, []);

  // Drag
  const handleDragStart = useCallback((e, booking) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setPopupBooking(null);
    const d = dragRef.current;
    const barRect = e.currentTarget.getBoundingClientRect();
    const duration = timeToMin(booking.end_time) - timeToMin(booking.start_time);
    const isSocial = booking.booking_type === "social";
    const grabOff = clientXToMinutes(e.clientX) - timeToMin(booking.start_time);
    const cfg = isSocial ? SOCIAL_CFG : (STATUS_CFG[booking.status] || STATUS_CFG.pending);

    const clone = document.createElement("div");
    clone.style.cssText = `position:fixed;pointer-events:none;z-index:9999;border-radius:14px;display:flex;align-items:center;padding:0 12px;font-size:13px;font-weight:700;width:${barRect.width}px;height:${barRect.height}px;left:${barRect.left}px;top:${barRect.top}px;opacity:0.9;box-shadow:0 4px 20px rgba(0,0,0,0.4);border:2px solid #3b82f6;color:#e2e8f0;background:rgba(30,41,59,0.85);backdrop-filter:blur(8px);border-left:3px solid ${cfg.bl};`;
    clone.textContent = booking.customer_name || booking.organizer_name;
    document.body.appendChild(clone);
    const tooltip = document.createElement("div");
    tooltip.style.cssText = "position:fixed;pointer-events:none;z-index:9999;background:#0f172a;border:1px solid #334155;color:#e2e8f0;font-size:11px;padding:2px 8px;border-radius:4px;box-shadow:0 4px 12px rgba(0,0,0,0.5);white-space:nowrap;display:none;";
    document.body.appendChild(tooltip);
    const highlight = document.createElement("div");
    highlight.style.cssText = "position:absolute;pointer-events:none;z-index:5;border-radius:4px;background:rgba(59,130,246,0.15);border:1px dashed rgba(59,130,246,0.5);display:none;";

    Object.assign(d, { active: false, bookingId: booking.id, booking, isSocial, duration,
      grabOffsetMinutes: grabOff, cloneEl: clone, tooltipEl: tooltip, highlightEl: highlight,
      origCourtId: booking.court_id, origStart: booking.start_time, origEnd: booking.end_time,
      newCourtId: booking.court_id, newStart: booking.start_time, newEnd: booking.end_time,
      startX: e.clientX, startY: e.clientY, grabClientOffsetX: e.clientX - barRect.left, grabClientOffsetY: e.clientY - barRect.top });

    const onMove = (ev) => {
      if (!d.active && (Math.abs(ev.clientX - d.startX) > 5 || Math.abs(ev.clientY - d.startY) > 5)) {
        d.active = true; setDraggingId(booking.id); gridContainerRef.current?.appendChild(d.highlightEl);
      }
      if (!d.active) return;
      requestAnimationFrame(() => {
        if (!d.cloneEl) return;
        d.cloneEl.style.left = `${ev.clientX - d.grabClientOffsetX}px`; d.cloneEl.style.top = `${ev.clientY - d.grabClientOffsetY}px`;
        const raw = clientXToMinutes(ev.clientX) - d.grabOffsetMinutes;
        const clamped = Math.max(START_HOUR * 60, Math.min((END_HOUR * 60) - d.duration, Math.round(raw / 15) * 15));
        d.newStart = minToTime(clamped); d.newEnd = minToTime(clamped + d.duration);
        let newCid = d.origCourtId;
        if (!d.isSocial) { const t = findCourtAtY(ev.clientY); if (t && t.court_type !== "social") newCid = t.id; }
        d.newCourtId = newCid;
        const cn = courts.find(c => c.id === newCid)?.name || "";
        d.tooltipEl.textContent = `${d.newStart} - ${d.newEnd} | ${cn}`; d.tooltipEl.style.display = "block"; d.tooltipEl.style.left = `${ev.clientX + 14}px`; d.tooltipEl.style.top = `${ev.clientY - 28}px`;
        const rowEl = rowRefs.current[newCid];
        if (rowEl && d.highlightEl) { const hlL = (clamped - START_HOUR * 60) * PX_PER_MIN, hlW = d.duration * PX_PER_MIN, rr = rowEl.getBoundingClientRect(), cr2 = gridContainerRef.current?.getBoundingClientRect();
          if (cr2) { d.highlightEl.style.display = "block"; d.highlightEl.style.top = `${rr.top - cr2.top + 2}px`; d.highlightEl.style.height = `${rr.height - 4}px`; d.highlightEl.style.left = `${hlL + SIDEBAR_WIDTH}px`; d.highlightEl.style.width = `${hlW}px`; } }
        Object.entries(rowRefs.current).forEach(([c, el]) => { if (el) { c === newCid ? el.classList.add("drag-over-row") : el.classList.remove("drag-over-row"); } });
      });
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp);
      const wasDrag = d.active;
      const s = { id: d.bookingId, bk: d.booking, nc: d.newCourtId, ns: d.newStart, ne: d.newEnd, oc: d.origCourtId, os: d.origStart, oe: d.origEnd };
      cleanupDrag(); setDraggingId(null);
      if (!wasDrag) {
        // Only show popup if onBookingClick is defined (not employee mode)
        if (s.bk && onBookingClick) { const barEl = document.querySelector(`[data-testid="booking-bar-${s.id}"]`); setPopupPos(calcPopupPos(barEl)); setPopupBooking(s.bk); }
        return;
      }
      justDraggedRef.current = true; setTimeout(() => { justDraggedRef.current = false; }, 100);
      if (s.nc === s.oc && s.ns === s.os && s.ne === s.oe) return;
      setOptimisticBookings(bookings.map(b => b.id === s.id ? { ...b, court_id: s.nc, court_name: courts.find(c => c.id === s.nc)?.name || b.court_name, start_time: s.ns, end_time: s.ne } : b));
      try { await api.put(`/bookings/${s.id}`, { court_id: s.nc, start_time: s.ns, end_time: s.ne }); toast.success("Di chuyển đặt sân thành công"); if (onSaved) onSaved(); }
      catch (err) { setOptimisticBookings(null); toast.error(err.response?.status === 409 ? "Trùng lịch! Không thể di chuyển" : "Lỗi khi di chuyển đặt sân"); }
      setTimeout(() => setOptimisticBookings(null), 600);
    };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  }, [bookings, courts, cleanupDrag, onSaved, clientXToMinutes, findCourtAtY, calcPopupPos, onBookingClick]);

  // Slot clicks
  const handleSlotClick = useCallback((courtId, e) => {
    if (dragRef.current.active || justDraggedRef.current) return;
    if (e.target.hasAttribute("data-booking-bar") || e.target.closest("[data-booking-bar]")) return;
    setPopupBooking(null);
    const r = e.currentTarget.getBoundingClientRect();
    const totalMin = ((e.clientX - r.left) / PX_PER_MIN) + START_HOUR * 60;
    const clamped = Math.max(START_HOUR * 60, Math.min(23 * 60 + 30, Math.round(totalMin / 30) * 30));
    onSlotClick(courtId, minToTime(clamped));
  }, [onSlotClick]);

  const handleSocialExpandedSlotClick = useCallback((e) => {
    if (dragRef.current.active || justDraggedRef.current) return;
    if (e.target.hasAttribute("data-booking-bar") || e.target.closest("[data-booking-bar]")) return;
    e.stopPropagation(); setPopupBooking(null);
    if (!socialCourt) return;
    const r = e.currentTarget.getBoundingClientRect();
    const totalMin = ((e.clientX - r.left) / PX_PER_MIN) + START_HOUR * 60;
    const clamped = Math.max(START_HOUR * 60, Math.min(23 * 60 + 30, Math.round(totalMin / 30) * 30));
    if (onSocialSlotClick) onSocialSlotClick(socialCourt.id, minToTime(clamped));
  }, [onSocialSlotClick, socialCourt]);

  const showCurrentLine = currentMinute >= START_HOUR * 60 && currentMinute < END_HOUR * 60;
  const isToday = selectedDate === new Date().toISOString().split("T")[0];
  const currentTimePx = (currentMinute - START_HOUR * 60) * PX_PER_MIN;
  const currentTimeStr = minToTime(currentMinute);
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => START_HOUR + i);
  const socialBookings = socialCourt ? (bookingsByCourtId[socialCourt.id] || []) : [];
  const sortedSocialBookings = useMemo(() =>
    [...socialBookings].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at) : 0;
      const db = b.created_at ? new Date(b.created_at) : 0;
      return db - da;
    }), [socialBookings]);

  const gridLines = useMemo(() => (
    <>
      {hours.map(h => <div key={h} className="absolute top-0 bottom-0 timeline-gridline-hour" style={{ left: (h - START_HOUR) * HOUR_WIDTH }} />)}
      {hours.map(h => [1, 2, 3].map(q => <div key={`${h}-${q}`} className="absolute top-0 bottom-0 timeline-gridline-quarter" style={{ left: (h - START_HOUR) * HOUR_WIDTH + q * QUARTER_WIDTH }} />))}
      {peakWidthPx > 0 && <div className="absolute top-0 bottom-0 bg-purple-500/5" style={{ left: peakLeftPx, width: peakWidthPx }} />}
    </>
  ), [hours, peakLeftPx, peakWidthPx]);

  // Social main row: empty when collapsed, bars when expanded
  const renderSocialMainRow = (court) => {
    const revenue = courtRevenue(court.id);
    const totalSocialGuests = socialBookings
      .filter(b => b.status !== "cancelled")
      .reduce((sum, b) => sum + (b.social_guest_count || 1), 0);
    return (
      <div key={court.id} className="flex" style={{ height: ROW_HEIGHT, background: "linear-gradient(135deg, rgba(13,148,136,0.15), rgba(15,118,110,0.08))" }} data-testid={`timeline-row-${court.id}`} ref={el => { rowRefs.current[court.id] = el; }}>
        <div className="sticky left-0 z-20 w-[180px] shrink-0 border-r border-b border-[#334155] px-3 py-2 flex flex-col justify-center timeline-sidebar-cell" style={{ borderLeft: "3px solid #2dd4bf" }}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold font-heading" style={{ color: "#2dd4bf" }}>{court.name}</span>
            <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30 text-[10px] px-1.5 py-0">Social</Badge>
            <button onClick={(e) => { e.stopPropagation(); setSocialExpanded(!socialExpanded); }} className="ml-auto text-teal-400 hover:text-teal-300" data-testid="toggle-social-expand">
              {socialExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-teal-400">{totalSocialGuests} khách social</span>
            {!hidePrice && <span className="text-[10px] text-lime-400">{formatVND(revenue)}đ</span>}
          </div>
        </div>
        <div className="relative border-b border-[#334155] flex-1 cursor-crosshair" style={{ minWidth: TOTAL_HOURS * HOUR_WIDTH, overflow: "hidden" }}
          onClick={(e) => handleSlotClick(court.id, e)} data-testid={`timeline-area-${court.id}`}>
          {socialExpanded ? (
            <>{gridLines}</>
          ) : (
            <>{gridLines}</>
          )}
        </div>
      </div>
    );
  };

  const renderCourtRow = (court, idx) => {
    const cB = bookingsByCourtId[court.id] || [];
    return (
      <div key={court.id} className="flex" style={{ height: ROW_HEIGHT }} data-testid={`timeline-row-${court.id}`} ref={el => { rowRefs.current[court.id] = el; }}>
        <div className="sticky left-0 z-20 w-[180px] shrink-0 border-r border-b border-[#334155] px-3 py-2 flex flex-col justify-center timeline-sidebar-cell">
          <div className="flex items-center gap-2"><span className="text-sm font-semibold text-[#F8FAFC] font-heading">{court.name}</span></div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-500">{courtBookingCount(court.id)} đặt sân</span>
            {!hidePrice && <span className="text-[10px] text-lime-400">{formatVND(courtRevenue(court.id))}đ</span>}
          </div>
        </div>
        <div className="relative border-b border-[#334155] flex-1 cursor-crosshair" style={{ minWidth: TOTAL_HOURS * HOUR_WIDTH, overflow: "hidden" }}
          onClick={(e) => handleSlotClick(court.id, e)} data-testid={`timeline-area-${court.id}`}>
          {gridLines}
          {cB.map(b => <BookingBar key={b.id} booking={b} onDragStart={handleDragStart} isDraggingThis={draggingId === b.id} hidePrice={hidePrice} hideName={hideName} isNewest={b.id === newestBookingId} />)}
        </div>
      </div>
    );
  };

  const renderSocialSubRow = (booking) => (
    <div key={`sub-${booking.id}`} className="flex" style={{ height: ROW_HEIGHT - 8 }}>
      <div className="sticky left-0 z-20 w-[180px] shrink-0 border-r border-b border-[#334155]/60 px-3 py-1 flex flex-col justify-center timeline-sidebar-cell">
        <span className="text-xs text-teal-300 truncate">{booking.organizer_name || booking.customer_name}</span>
        <span className="text-[10px] text-slate-500">{booking.social_guest_count} khách</span>
      </div>
      <div className="relative border-b border-[#334155]/40 flex-1 cursor-crosshair" style={{ minWidth: TOTAL_HOURS * HOUR_WIDTH, overflow: "hidden" }} onClick={handleSocialExpandedSlotClick}>
        {hours.map(h => <div key={h} className="absolute top-0 bottom-0 timeline-gridline-hour" style={{ left: (h - START_HOUR) * HOUR_WIDTH }} />)}
        <BookingBar booking={booking} onDragStart={handleDragStart} isDraggingThis={draggingId === booking.id} hidePrice={hidePrice} hideName={hideName} isNewest={booking.id === newestBookingId} />
      </div>
    </div>
  );

  const headerScrollRef = React.useRef(null);

  // Sync horizontal scroll between header and body
  useEffect(() => {
    const body = scrollRef.current;
    const header = headerScrollRef.current;
    if (!body || !header) return;
    let ticking = false;
    const onBodyScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(() => { header.scrollLeft = body.scrollLeft; ticking = false; }); }
    };
    const onHeaderScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(() => { body.scrollLeft = header.scrollLeft; ticking = false; }); }
    };
    body.addEventListener("scroll", onBodyScroll);
    header.addEventListener("scroll", onHeaderScroll);
    return () => { body.removeEventListener("scroll", onBodyScroll); header.removeEventListener("scroll", onHeaderScroll); };
  }, []);

  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden relative flex flex-col" data-testid="timeline-grid" style={{ maxHeight: "calc(100vh - 220px)" }}>
      {/* Sticky header row - always visible */}
      <div ref={headerScrollRef} className="overflow-x-auto shrink-0 z-40 hide-scrollbar timeline-header">
        <div className="flex min-w-fit" style={{ height: 36 }}>
          <div className="sticky left-0 z-40 w-[180px] shrink-0 border-r border-b border-[#334155] px-3 flex items-center timeline-sidebar-cell">
            <span className="text-xs text-slate-500 font-medium">Sân / Giờ</span>
          </div>
          <div className="relative border-b border-[#334155] timeline-header" style={{ minWidth: TOTAL_HOURS * HOUR_WIDTH }}>
            {hours.map(h => {
              const isPeakHour = h >= PEAK_HOUR_START && h < PEAK_HOUR_END;
              return (
                <div key={h} className={`absolute top-0 bottom-0 flex items-center timeline-gridline-hour`} style={{ left: (h - START_HOUR) * HOUR_WIDTH, width: HOUR_WIDTH, backgroundColor: isPeakHour ? "rgba(139, 92, 246, 0.12)" : undefined }}>
                  <span className={`text-[11px] pl-1.5 font-medium ${isPeakHour ? "text-purple-300" : "text-slate-400"}`}>{String(h).padStart(2, "0")}:00</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable body - vertical + horizontal */}
      <div className="overflow-x-auto overflow-y-auto flex-1" ref={scrollRef}>
        <div className="flex flex-col min-w-fit relative" ref={gridContainerRef} onMouseMove={handleTimelineMouseMove} onMouseLeave={handleTimelineMouseLeave}>
          {socialCourt && <>{renderSocialMainRow(socialCourt)}{socialExpanded && sortedSocialBookings.map(b => renderSocialSubRow(b))}</>}
          {regularCourts.map((court, idx) => renderCourtRow(court, idx))}

          {/* Peak hours highlight 17:00-19:00 */}
          {peakWidthPx > 0 && (
            <div className="absolute pointer-events-none z-[2]" style={{ left: peakLeftPx + SIDEBAR_WIDTH, top: 0, width: peakWidthPx, height: "100%", backgroundColor: "rgba(139, 92, 246, 0.08)" }} data-testid="peak-hours-highlight" />
          )}

          {/* Current time marker */}
          {showCurrentLine && isToday && (
            <div className="absolute z-[45] pointer-events-none" style={{ left: currentTimePx + SIDEBAR_WIDTH, top: 0, bottom: 0 }} data-testid="current-time-line">
              <div className="absolute -top-0 -translate-x-1/2 bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded z-[51] whitespace-nowrap" style={{ left: 1 }}>{currentTimeStr}</div>
              <div className="absolute top-[22px] -ml-[4px] w-[10px] h-[10px] rounded-full bg-red-500 z-[51]" />
              <div className="absolute top-[26px] bottom-0 w-[2px] bg-red-500" />
            </div>
          )}

          {/* Hover elements */}
          <div ref={hoverHighlightRef} className="absolute top-[36px] bottom-0 pointer-events-none z-[4]" style={{ display: "none", width: QUARTER_WIDTH, background: "rgba(59,130,246,0.06)" }} />
          <div ref={hoverLineRef} className="absolute top-[36px] bottom-0 pointer-events-none z-[4]" style={{ display: "none", width: 1, background: "rgba(59,130,246,0.35)" }} />
          <div ref={hoverTooltipRef} className="absolute pointer-events-none z-[50] text-white text-[11px] font-bold px-2 py-0.5 rounded" style={{ display: "none", top: 6, background: "#3b82f6" }} />
        </div>
      </div>
      <BookingPopup booking={popupBooking} position={popupPos} onClose={() => setPopupBooking(null)}
        onStatusChange={handleStatusChange} onEdit={(b) => { setPopupBooking(null); onBookingClick(b); }} onDelete={handlePopupDelete} onVerifyPayment={onVerifyPayment} onUnverifyPayment={onUnverifyPayment} />
      <PinDialog open={deletePinOpen} onOpenChange={setDeletePinOpen} onConfirm={doDeleteBooking} title="Xác nhận xóa đặt sân" description="Nhập mã PIN để xóa đặt sân này." />
    </div>
  );
}
